import React, { useCallback, useEffect, useState } from 'react'
import { Stack } from 'react-bootstrap'
import { getOnboardingContactMappingCategory, getIntegrationDisplayName, type IntegrationServiceId } from '../../../../common/integrations'
import { CONTACT_SYNC_COPY } from '../../copies/contactSyncCopy'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useGetAccountQuery } from '../../../../store/accountUserSettings/accountUserSettings'
import { useZocksAuth } from '../../../../components/ScopedBlock/hooks'
import { useHasIntegrationConnectScope } from '../../../../components/Integrations/useHasIntegrationConnectScopeAndQuota'
import { type CrmIntegrationSettings } from '../../../Integration/IntegrationManagement/OnboardingSupport/types'
import { PostConnectStepFrame } from '../PostConnectStepFrame'
import { OnboardingStepHeader } from '../../Onboarding/OnboardingStepHeader/OnboardingStepHeader'
import { ContactSyncAdminControl } from './controls/ContactSyncAdminControl'
import { ContactSyncUserControl } from './controls/ContactSyncUserControl'
import { getOnboardingCrmSettingsProvider } from '../../../Integration/IntegrationManagement/OnboardingSupport/settingsProviders'
import { useIntegrations } from '../../../Integration/useIntegrations'
import { POST_CONNECT_COPIES } from '../../copies/onboardingCopies'

export interface ContactSyncStepProps {
  serviceId: IntegrationServiceId
  useConnectedHeader: boolean
  onContinue: () => void
  onExit: () => void
  onNavigateToSettings: () => void
  onBack?: () => void
}

interface ContactSyncStepViewProps extends ContactSyncStepProps {
  settings: CrmIntegrationSettings
  isAdmin: boolean
  isEnterprise: boolean
}

const ContactSyncStepView: React.FC<ContactSyncStepViewProps> = ({
  serviceId,
  settings,
  isAdmin,
  isEnterprise,
  useConnectedHeader,
  onContinue,
  onExit,
  onNavigateToSettings,
  onBack,
}) => {
  const { isIntegrationSyncDisabled, isLoading: isIntegrationsLoading } = useIntegrations()

  const { isFetching, isSaving, save } = settings

  const isSyncDisabled = isIntegrationSyncDisabled(serviceId)
  const displayName = getIntegrationDisplayName(serviceId)
  const category = getOnboardingContactMappingCategory(serviceId)

  const categoryCopy = CONTACT_SYNC_COPY[category]

  const isAdminEligible = isAdmin && !isEnterprise
  const showRadio = (categoryCopy.showRadioForAllUsers === true || isAdminEligible) && categoryCopy.radio !== undefined
  const radioCopy = categoryCopy.radio
  const toggleCopy = categoryCopy.toggle

  const defaultOn = (showRadio ? categoryCopy.radio?.default : categoryCopy.toggle?.default) || false
  const [localContactSync, setLocalContactSync] = useState(defaultOn)

  const handleSave = useCallback(async () => {
    await save({ contactSync: localContactSync })
  }, [save, localContactSync])

  const hasDualActionFooter = showRadio && !localContactSync
  const nextLabel = hasDualActionFooter ? radioCopy!.dualPrimaryLabel : showRadio ? radioCopy!.singleActionLabel : toggleCopy!.buttonLabel

  useEffect(() => {
    if (!isIntegrationsLoading && isSyncDisabled) {
      onContinue()
    }
  }, [isIntegrationsLoading, isSyncDisabled, onContinue])

  return (
    <PostConnectStepFrame
      loading={isFetching}
      onSave={hasDualActionFooter ? undefined : handleSave}
      onAdvance={hasDualActionFooter ? onNavigateToSettings : onContinue}
      nextLabel={nextLabel}
      isNextDisabled={isSaving}
      skipAction={hasDualActionFooter ? { label: radioCopy!.dualSecondaryLabel, onClick: onExit, disabled: isSaving } : undefined}
      onBack={onBack}
    >
      <OnboardingStepHeader
        icon={useConnectedHeader ? <i className='bi bi-check-circle text-success' /> : <i className='bi bi-sliders2' />}
        title={useConnectedHeader ? `${displayName} is connected` : `Sync contacts from ${displayName}`}
        subtitle={POST_CONNECT_COPIES['contact-sync'].headerSubtitle}
      />
      <p className='m-0 fw-bold fs-5 text-center text-dark'>Import your contacts</p>

      <Stack gap={2}>
        {showRadio ? (
          <ContactSyncAdminControl copy={radioCopy!} contactSync={localContactSync} onChange={setLocalContactSync} />
        ) : (
          <ContactSyncUserControl
            syncOnDescription={toggleCopy!.syncOnDescription(isEnterprise)}
            contactSync={localContactSync}
            onChange={setLocalContactSync}
          />
        )}
      </Stack>
    </PostConnectStepFrame>
  )
}

export const ContactSyncStep: React.FC<ContactSyncStepProps> = ({ serviceId, useConnectedHeader, onContinue, onExit, onNavigateToSettings, onBack }) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: account, isLoading: isAccountLoading } = useGetAccountQuery({ accountId })
  const isEnterprise = !!account?.data?.parentId
  const hasConnectScope = useHasIntegrationConnectScope(serviceId)
  const { hasScope } = useZocksAuth()
  const isAdmin = hasConnectScope && hasScope('**', 'account:update', ['account:admin'])

  const Provider = getOnboardingCrmSettingsProvider(serviceId)
  if (!Provider || isAccountLoading) {
    return null
  }

  return (
    <Provider>
      {(settings: CrmIntegrationSettings) => (
        <ContactSyncStepView
          serviceId={serviceId}
          settings={settings}
          isAdmin={isAdmin}
          isEnterprise={isEnterprise}
          useConnectedHeader={useConnectedHeader}
          onContinue={onContinue}
          onExit={onExit}
          onNavigateToSettings={onNavigateToSettings}
          onBack={onBack}
        />
      )}
    </Provider>
  )
}
